import { CONTACT_TYPE, OrderDetails, OrderTemplate } from "@/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFields, useModal, useNotes } from "@/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { ChangePackageModal } from "@/components/organisms/Modais/ChangePackage";
import { LogoParams } from "@/api/order/interface";
import { OrderCustomer } from "@/components/pages";
import { extraText } from "@/helpers";
import { useApi } from "@/api";

const OrderCustomerView = () => {
  const { order } = useParams<'order'>();

  const navigate = useNavigate();
  
  const { customer, order: orderEntity, orderTemplate: orderTemplateApi, session } = useApi();
  
  const [loadingOrderTemplates, setLoadingOrderTemplates] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [details, setDetails] = useState<OrderDetails>();
  const { notes, onAddNote, setNotes } = useNotes();
  

  const getOrderDetails = async (value: string) => {
    await orderEntity.getDetailedOrderData(value);
    const n = await orderEntity.getNotes(value);
    setNotes(n);
  }
  
  const isAdmin = useMemo(() => session.user?.admin || false, [session.user]);
    
  const {
    updateFields,
    ...fields
  } = useFields();

  const {
    showModal: showChangePackageModal,
    onCloseModal: onCloseChangePackageModal,
    onOpenModal: onOpenChangePackageModal 
  } = useModal();

  useEffect(() => {
    if (order && !fetched) {
      getOrderDetails(order);
      setFetched(true);
    }
  }, [order, orderEntity.orderDetails]);

  useEffect(() => {
    const { orderDetails } = orderEntity;

    if (orderDetails) {
      setDetails(orderDetails);
      updateFields();   
    }
  }, [order, orderEntity.orderDetails]);

  useEffect(() => {
    customer.getLogo(void 0, true);
  }, [order]);
  
const handleConfirmFiles = useCallback(async (serviceId: string, files: string[], isDraft: boolean) => {
  if (isDraft)
    orderEntity.confirmDraftFiles(order || '', files);
  else
    orderEntity.confirmFiles(order || '', files);

  if (order) {
    return getOrderDetails(order);
  }
}, [orderEntity.orderDetails]);
  
const handleChooseDraftFiles = useCallback(async (files: string[]) => {
  orderEntity.addDraftFiles(order || '', files);

  if (order) {
    return getOrderDetails(order);
  }
}, [orderEntity.orderDetails]);

const handleDownloadFiles = useCallback((serviceId: string, filesId: string[], isDraft: boolean,  resolutions?: string[], logoParams?: LogoParams) => {
  if (order) {
    const resObjects = resolutions?.map(r => { return { "name": r } }) || [];
    orderEntity.downloadOrderFiles(order, serviceId, resObjects, filesId, isDraft, logoParams);
  }
}, [order]);

  const handleNope = () => true;

  const handleNopeAsync = async () => Promise.resolve();

  const handleGetFloorAsync = async () => {
    return 0;
  }

  const handleAddFurnitures = async (orderServiceId : string) => {
    orderEntity.addVirtualFurniture(orderServiceId ,fields.furnitureNames.value);
  }

  const handleUpdatePhotoCount = async (count : number) => {
    if (order)
      orderEntity.updatePhotoCount(order, count);
  }

  const handleCancelNote = useCallback(async () => {
    setNotes(notes.slice(0, -1));
  }, [notes]);

  const handleSaveNote = useCallback(async (noteContent: string) => {
    const lastNote = notes[notes.length - 1];
    lastNote.content = noteContent;
    lastNote.created = new Date().toISOString();
    orderEntity.addNote(order || '', noteContent);
    setNotes([...notes.slice(0, -1), lastNote]);
  },[notes]);

  const handleChangePackage = async() => {
    setLoadingOrderTemplates(true);
    onOpenChangePackageModal();
    const branches = await customer.getBranches(session.user?.id || '');
    orderTemplateApi.getList(branches);
    setLoadingOrderTemplates(false);
  }

  const handleShareOrder = (email) => {
    orderEntity.shareOrder(order || '', email);
  }
  
  const handleSelectOrderTemplate = async (orderTemplate: OrderTemplate) => {
      await orderEntity.changeTemplate(orderEntity.orderDetails?.id || '', orderTemplate.id);
      await orderEntity.getDetailedOrderData(orderEntity.orderDetails?.id || '');
  }

  const handleNavigateToAdmin = () => {
    navigate(`/order-view-backend/order=${order}`);
  }

  const handleNavigateToAWorker = () => {
    navigate(`/order-view-worker/order=${order}`);
  }

  return (
    <>
      {details && <OrderCustomer
        {...fields}
        extraText={extraText(details.orderTemplateId)}
        isAdmin={isAdmin}
        contacts={details.contacts.map(o => {
          return {contactId: o.id,
          customerId: details.customerId || '',
          email: o.email,
          name: o.name,
          phone1: o.phone1,
          phone2: o.phone2 || '',
          sendInvoice: false,
          type: o.type || CONTACT_TYPE.VENDOR }
        } ) || []}
        logoUrl={customer.logoUrl}
        notes={notes || []}
        onAddFurnitures={handleAddFurnitures}
        onUpdatePhotoCount={handleUpdatePhotoCount}
        onAddNote={onAddNote}
        onCancelNote={handleCancelNote}
        onSaveNote={handleSaveNote}
        onAskToChangeWorker={handleNope}
        onChangePackage={handleChangePackage}
        onConfirmFiles={handleConfirmFiles}
        onChooseDraftFiles={handleChooseDraftFiles}
        onDeleteFiles={handleNopeAsync}
        onDownloadFiles={handleDownloadFiles}
        onGetFloorArea={handleGetFloorAsync}
        onNavigateToAdmin={handleNavigateToAdmin}
        onNavigateToWorker={handleNavigateToAWorker}
        onRecordFloorArea={handleNopeAsync}
        onShareOrder={handleShareOrder}
        orderServiceId={""}
        coordinator={null}
        onTakeRole={handleNope}
        onUploadFiles={handleNopeAsync}
        onChangeRole={handleNope}
        offeredServices={details.offeredServices || []}
        orderDetails={details}
        preferredWorker={null}
        services={details.services || []}
        owner= {`${session.user?.firstName} ${session.user?.lastName}`}
      /> }
      {showChangePackageModal && (
        <ChangePackageModal
          isAdmin={isAdmin}
          loading={loadingOrderTemplates}
          onClose={onCloseChangePackageModal}
          onSelectOrderTemplate={handleSelectOrderTemplate}
          orderTemplates={orderTemplateApi.orderTemplates}
        />) }
    </>
  );
}

export default OrderCustomerView;